import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, ArrowRight, Users, GitBranch, FileText, Rocket, X, Sparkles } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { Switch } from './ui/switch';
import { Label } from './ui/label';

interface OnboardingWizardProps {
  companyName: string;
  subdomain: string;
  onComplete: () => void;
  onSkip: () => void;
}

export function OnboardingWizard({ companyName, subdomain, onComplete, onSkip }: OnboardingWizardProps) {
  const [step, setStep] = useState(0);
  const [sowGenerationEnabled, setSOWGenerationEnabled] = useState(true);

  const steps = [
    {
      title: 'Welcome to PathKit',
      icon: Rocket,
      content: (
        <div className="space-y-4">
          <p>
            Your Pathkit instance is ready at <strong>{subdomain}.pathkit.pro</strong>
          </p>
          <p>
            PathKit helps Atlassian consultants configure Jira Service Management faster and more accurately. Begin with a quick tour of key features.
          </p>
          <Alert>
            <AlertDescription>
              This wizard will take about 2 minutes and will help you understand the key features.
            </AlertDescription>
          </Alert>
        </div>
      )
    },
    {
      title: 'Create Your First Project',
      icon: GitBranch,
      content: (
        <div className="space-y-4">
          <p>
            In PathKit, everything is organized around <strong>Projects</strong> (which map to Jira Spaces).
          </p>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h4 className="font-semibold mb-2">What you can do:</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                <span>Create multiple projects for different clients or departments</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                <span>Define workflows, work types, and teams for each project</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                <span>Track implementation progress and mark items as ready</span>
              </li>
            </ul>
          </div>
          <p className="text-sm text-gray-600">
            <strong>Next step:</strong> Go to the Projects page and click "Create New Project"
          </p>
        </div>
      )
    },
    {
      title: 'Manage Scope with SOWs',
      icon: FileText,
      content: (
        <div className="space-y-4">
          <p>
            Track your <strong>Statements of Work</strong> and change orders to ensure you stay in scope.
          </p>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h4 className="font-semibold mb-2">SOW Features:</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                <span>Create multiple SOWs per company</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                <span>Track change orders and scope changes</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                <span>Link projects to SOWs for better tracking</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                <span>Monitor scope variance in real-time</span>
              </li>
            </ul>
          </div>
          <p className="text-sm text-gray-600">
            <strong>Next step:</strong> Navigate to SOWs and create your first statement of work
          </p>
        </div>
      )
    },
    {
      title: 'Invite Your Team',
      icon: Users,
      content: (
        <div className="space-y-4">
          <p>
            <strong>Invite consultants and team members</strong> to work together on client implementations.
          </p>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h4 className="font-semibold mb-2">Role Types:</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <strong>Company Admin:</strong> Full access to your company's data
              </li>
              <li>
                <strong>Consultant:</strong> Can work on assigned client projects
              </li>
              <li>
                <strong>User:</strong> View-only access for stakeholders
              </li>
            </ul>
          </div>
          <p className="text-sm text-gray-600">
            <strong>Next step:</strong> Go to Settings → Users and click "Invite User"
          </p>
        </div>
      )
    },
    {
      title: 'Generate Jira Backlog',
      icon: Rocket,
      content: (
        <div className="space-y-4">
          <p>
            Once your configuration is ready, use the <strong>Jira Backlog Generator</strong> to create implementation stories in Jira.
          </p>
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <h4 className="font-semibold mb-2">Backlog Generator:</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-orange-600 mt-0.5" />
                <span>Automatically creates epics and stories in Jira</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-orange-600 mt-0.5" />
                <span>Generates acceptance criteria and subtasks</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-orange-600 mt-0.5" />
                <span>Assigns stories to consultants automatically</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-orange-600 mt-0.5" />
                <span>Filters by phase and ready status</span>
              </li>
            </ul>
          </div>
          <p className="text-sm text-gray-600">
            <strong>Next step:</strong> Configure your Jira credentials in the Jira Integration page
          </p>
        </div>
      )
    },
    {
      title: 'Setup Complete',
      icon: CheckCircle,
      content: (
        <div className="space-y-4">
          <p>
            You now have everything you need to start using PathKit.
          </p>
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg border border-blue-200">
            <h4 className="font-semibold mb-3">Quick Actions:</h4>
            <div className="space-y-2 text-sm">
              <Button variant="outline" className="w-full justify-start" onClick={onComplete}>
                <GitBranch className="w-4 h-4 mr-2" />
                Create Your First Project
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={onComplete}>
                <FileText className="w-4 h-4 mr-2" />
                Set Up Your First SOW
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={onComplete}>
                <Users className="w-4 h-4 mr-2" />
                Invite Team Members
              </Button>
            </div>
          </div>
          <Alert>
            <AlertDescription>
              Need help? Check out our documentation or contact support at support@pathkit.pro
            </AlertDescription>
          </Alert>
        </div>
      )
    }
  ];

  const currentStep = steps[step];
  const Icon = currentStep.icon;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
      <Card className="max-w-2xl w-full">
        <CardHeader>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Icon className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <CardTitle>{currentStep.title}</CardTitle>
                <CardDescription>
                  Step {step + 1} of {steps.length}
                </CardDescription>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onSkip}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((step + 1) / steps.length) * 100}%` }}
            />
          </div>
        </CardHeader>

        <CardContent>
          <div className="mb-6">
            {currentStep.content}
          </div>

          <div className="flex gap-3">
            {step > 0 && (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
              >
                Back
              </Button>
            )}
            
            <div className="flex-1" />

            {step < steps.length - 1 ? (
              <>
                <Button
                  variant="ghost"
                  onClick={onSkip}
                >
                  Skip Tour
                </Button>
                <Button
                  onClick={() => setStep(step + 1)}
                >
                  Next <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </>
            ) : (
              <Button
                onClick={onComplete}
                size="lg"
              >
                Get Started <Rocket className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}